import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Medal, TrendUp, TrendDown, Minus, CaretRight, Cow, Globe } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MARKETS = [
  { id: "ireland", name: "Ireland", flag: "🇮🇪", endpoint: "/prices/irish-marts", link: "/irish-marts", currency: "€" },
  { id: "uk", name: "United Kingdom", flag: "🇬🇧", endpoint: "/uk/marts", link: "/uk-markets", currency: "£" },
  { id: "usa", name: "United States", flag: "🇺🇸", endpoint: "/usa/markets", link: "/usa-markets", currency: "$" },
];

// Link header to main Marts page
const MARTS_PAGE_LINK = "/marts";

const CATEGORIES = {
  ireland: [
    { id: "bullocks_400_500kg", name: "Bullocks 400-500kg" },
    { id: "bullocks_500_600kg", name: "Bullocks 500-600kg" },
    { id: "heifers_400_500kg", name: "Heifers 400-500kg" },
    { id: "heifers_500_600kg", name: "Heifers 500-600kg" },
    { id: "weanlings_300_400kg", name: "Weanlings 300-400kg" },
  ],
  uk: [
    { id: "steers_400_500kg", name: "Steers 400-500kg" },
    { id: "steers_500_600kg", name: "Steers 500-600kg" },
    { id: "heifers_400_500kg", name: "Heifers 400-500kg" },
  ],
  usa: [
    { id: "fed_cattle", name: "Fed Cattle" },
    { id: "feeder_cattle", name: "Feeder Cattle" },
  ],
};

export default function TopOfTheMarts() {
  const [topMarts, setTopMarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [market, setMarket] = useState("ireland");
  const [category, setCategory] = useState("bullocks_400_500kg");

  useEffect(() => {
    // Reset category when market changes
    const defaultCategory = CATEGORIES[market]?.[0]?.id || "bullocks_400_500kg";
    setCategory(defaultCategory);
  }, [market]);

  const generatePlaceholderData = useCallback((marketId, currency) => {
    const placeholders = {
      usa: [
        { name: "Amarillo Livestock Auction", location: "Texas", price: 185.50, change: 2.3 },
        { name: "Oklahoma National Stockyards", location: "Oklahoma", price: 183.25, change: 1.8 },
        { name: "Joplin Regional Stockyards", location: "Missouri", price: 182.00, change: -0.5 },
        { name: "San Angelo Livestock Auction", location: "Texas", price: 181.75, change: 3.1 },
      ],
    };
    return (placeholders[marketId] || []).map(p => ({ ...p, currency }));
  }, []);

  const fetchTopMarts = useCallback(async () => {
    setLoading(true);
    const selectedMarket = MARKETS.find(m => m.id === market);
    
    try {
      let data = [];
      
      if (market === "ireland") {
        const res = await axios.get(`${API}/prices/irish-marts`, {
          params: { category, sort_by: "price_desc" }
        });
        const prices = res.data?.prices || [];
        data = prices.slice(0, 4).map(m => ({
          name: m.mart_name,
          location: m.county,
          price: m.price,
          change: m.change_percent,
          currency: "€"
        }));
      } else if (market === "uk") {
        const res = await axios.get(`${API}/uk/marts`, {
          params: { category, sort_by: "price_desc" }
        });
        data = (res.data.marts || []).slice(0, 4).map(m => ({
          name: m.mart_name,
          location: m.region,
          price: m.price,
          change: m.change_percent || 0,
          currency: "£"
        }));
      } else {
        // For other markets, show placeholder/simulated data
        data = generatePlaceholderData(market, selectedMarket.currency);
      }
      
      setTopMarts(data);
    } catch (error) {
      console.error("Error fetching marts:", error);
      const errMarket = MARKETS.find(m => m.id === market);
      setTopMarts(generatePlaceholderData(market, errMarket?.currency || "€"));
    } finally {
      setLoading(false);
    }
  }, [market, category, generatePlaceholderData]);

  useEffect(() => {
    fetchTopMarts();
  }, [fetchTopMarts]);

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy size={16} weight="fill" className="text-[#FBBF24]" />;
    if (index === 1) return <Medal size={16} weight="fill" className="text-[#8B8580]" />;
    if (index === 2) return <Medal size={16} weight="fill" className="text-[#CD7F32]" />;
    return <span className="w-4 h-4 flex items-center justify-center text-[10px] font-bold text-[#6B6661]">{index + 1}</span>;
  };

  const getChangeIcon = (change) => {
    if (change > 0) return <TrendUp size={12} className="text-green-600" />;
    if (change < 0) return <TrendDown size={12} className="text-red-600" />;
    return <Minus size={12} className="text-[#6B6661]" />;
  };

  const getChangeColor = (change) => {
    if (change > 0) return "text-green-600";
    if (change < 0) return "text-red-600";
    return "text-[#6B6661]";
  };

  const selectedMarket = MARKETS.find(m => m.id === market);
  const categories = CATEGORIES[market] || CATEGORIES.ireland;

  if (loading) {
    return (
      <div className="border border-[#E0D4C8] bg-white p-4 animate-pulse">
        <div className="h-6 bg-[#E5E7EB] w-1/2 mb-4"></div>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-8 bg-[#E5E7EB] mb-1 rounded"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="border border-[#E0D4C8] bg-white h-full flex flex-col">
      {/* Header - Links to main Marts page */}
      <Link 
        to={MARTS_PAGE_LINK}
        className="flex items-center justify-between px-3 py-2 border-b border-[#E0D4C8] bg-gradient-to-r from-[#022C22] to-[#064E3B] hover:from-[#064E3B] hover:to-[#0F766E] transition-all"
      >
        <div className="flex items-center gap-2">
          <Trophy size={18} weight="fill" className="text-[#FBBF24]" />
          <h3 className="font-serif text-base text-white">Top of the Marts</h3>
        </div>
        <span className="text-[9px] font-mono uppercase tracking-wider text-[#FBBF24]" title="Modelled estimates — live mart feeds are being rolled out" data-testid="top-marts-indicative">Indicative</span>
      </Link>

      {/* Market & Category Selectors */}
      <div className="px-3 py-1.5 border-b border-[#E0D4C8] bg-[#F9FAFB] flex items-center gap-2">
        <select
          value={market}
          onChange={(e) => setMarket(e.target.value)}
          className="text-[10px] bg-transparent border border-[#E0D4C8] rounded px-1.5 py-0.5 focus:outline-none text-[#33302E] cursor-pointer"
        >
          {MARKETS.map(m => (
            <option key={m.id} value={m.id}>{m.flag} {m.name}</option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-[10px] bg-transparent border-none focus:outline-none text-[#6B6661] cursor-pointer flex-1"
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Top 8 Table */}
      <div className="divide-y divide-[#E5E7EB] flex-1 overflow-auto">
        {topMarts.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-[#6B6661] text-xs">
            No market data available. Try refreshing.
          </div>
        ) : topMarts.map((mart, index) => (
          <div
            key={`${mart.name}-${index}`}
            className={`flex items-center gap-2 px-3 py-1.5 hover:bg-[#F9FAFB] transition-colors ${
              index === 0 ? "bg-[#FFFBEB]" : ""
            }`}
          >
            {/* Rank */}
            <div className="flex-shrink-0 w-4">
              {getRankIcon(index)}
            </div>

            {/* Mart Info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[11px] text-[#33302E] truncate">
                {mart.name}
              </div>
              <div className="text-[9px] text-[#6B6661]">{mart.location}</div>
            </div>

            {/* Price */}
            <div className="text-right flex-shrink-0">
              <div className="font-mono font-semibold text-[11px] text-[#33302E]">
                {mart.currency}{mart.price.toFixed(2)}
              </div>
              <div className={`flex items-center justify-end gap-0.5 text-[9px] ${getChangeColor(mart.change)}`}>
                {getChangeIcon(mart.change)}
                <span>{mart.change > 0 ? "+" : ""}{mart.change.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Link */}
      <Link
        to={selectedMarket?.link || "/irish-marts"}
        className="flex items-center justify-between px-3 py-2 bg-[#F9FAFB] border-t border-[#E0D4C8] text-[#0F766E] hover:bg-[#F0FDF4] transition-colors group"
        data-testid="view-all-marts"
      >
        <span className="text-xs font-medium flex items-center gap-2">
          <Globe size={14} />
          View All {selectedMarket?.name} Markets
        </span>
        <CaretRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}
